/**
 * `'global'` is reserved: it is always live. Every other value is a page or
 * dialog's own scope name, pushed with `useShortcutScope`; tests may push
 * scope names that never appear in the production registry.
 */
export type ShortcutScope = string;

export const GLOBAL_SCOPE: ShortcutScope = 'global';

/** Pushed while the cheat sheet is open, suspending every scope below it. */
export const CHEAT_SHEET_SCOPE: ShortcutScope = 'cheatSheet';

/**
 * `matchOn: 'code'` reads the event's physical key (`event.code`), so a
 * letter combination keeps working under the Georgian layout, where
 * `event.key` would be a Georgian character. `matchOn: 'key'` reads the
 * logical value (`event.key`) and is used for function keys and named keys
 * (`Enter`, `Escape`, `Delete`, the arrows), which report the same value in
 * every layout.
 */
export type ShortcutCombination = {
    matchOn: 'key' | 'code';
    value: string;
    ctrlKey?: boolean;
    shiftKey?: boolean;
    altKey?: boolean;
    metaKey?: boolean;
};

export type ShortcutDefinition = {
    id: string;
    /** For a `displayOnly` entry, the first key it stands for (`ArrowLeft` for `← / →`); nothing matches it. */
    combination: ShortcutCombination;
    scope: ShortcutScope;
    /**
     * Literal Latin keycap text, never localized. The parts of one
     * combination, joined with `+` when shown: `['F2']`, `['Ctrl', 'Shift', 'L']`.
     * Alternatives are one part: `['← / →']`, `['0-9 / .']`.
     */
    keycapLabels: string[];
    /**
     * Fully qualified i18n key for the action name,
     * `components:shortcuts.action.<name>` — the shared namespace, so any
     * caller's `t` resolves it whatever its own default namespace. The
     * registry never holds translated strings.
     */
    labelKey: string;
    /**
     * Fires even while an editable element has focus, the way a function key
     * always does — for a non-function-key binding whose whole point is to
     * work no matter what the seller was doing (e.g. locking the till from
     * mid-search). Opt in per entry rather than widening the function-key
     * exemption itself; defaults to `false`.
     */
    firesWhileEditable?: boolean;
    /**
     * Matched by the feature's own listener or the design system, never by
     * the provider: listed in the cheat sheet and rendered as hints, never
     * bound with `useShortcut` (the provider ignores it even if something
     * does). For keys that carry a value rather than a fixed command (PIN
     * and amount digits), keys whose action changes with the dialog's step
     * (the shift dialog's `Enter`), and a design-system component's own keys
     * (a list box's arrows and `Enter`, a dialog's `Esc`).
     */
    displayOnly?: boolean;
};

/** Reserved for the browser's own reload/fullscreen/devtools bindings; never in the registry, whatever the entry's `matchOn`. */
export const FORBIDDEN_KEY_VALUES = ['F5', 'F11', 'F12'];

type Modifiers = Pick<
    ShortcutCombination,
    'ctrlKey' | 'shiftKey' | 'altKey' | 'metaKey'
>;

export const modifiersEqual = (a: Modifiers, b: Modifiers): boolean =>
    Boolean(a.ctrlKey) === Boolean(b.ctrlKey) &&
    Boolean(a.shiftKey) === Boolean(b.shiftKey) &&
    Boolean(a.altKey) === Boolean(b.altKey) &&
    Boolean(a.metaKey) === Boolean(b.metaKey);

export const combinationsEqual = (
    a: ShortcutCombination,
    b: ShortcutCombination,
): boolean =>
    a.matchOn === b.matchOn && a.value === b.value && modifiersEqual(a, b);

/** An entry's `labelKey`: the action's name in the shared `components` namespace. */
const action = (name: string): string => `components:shortcuts.action.${name}`;

/*
 * The sale screen (`seller`, pushed by `SellerShortcutScope`) — bound
 * entries first, then the keys the screen's own listeners and design-system
 * collections read.
 */

export const FOCUS_SEARCH_SHORTCUT: ShortcutDefinition = {
    id: 'seller.focusSearch',
    combination: { matchOn: 'key', value: 'F2' },
    scope: 'seller',
    keycapLabels: ['F2'],
    labelKey: action('focusSearch'),
};

export const CHEAT_SHEET_SHORTCUT: ShortcutDefinition = {
    id: 'global.cheatSheet',
    combination: { matchOn: 'key', value: 'F1' },
    scope: GLOBAL_SCOPE,
    keycapLabels: ['F1'],
    labelKey: action('cheatSheet'),
};

export const CATEGORY_FILTER_SHORTCUT: ShortcutDefinition = {
    id: 'seller.categoryFilter',
    combination: { matchOn: 'key', value: 'F3' },
    scope: 'seller',
    keycapLabels: ['F3'],
    labelKey: action('categoryFilter'),
};

/** Focuses the cart's last line; an empty cart shows a notice instead. See `CartPanel`. */
export const FOCUS_CART_SHORTCUT: ShortcutDefinition = {
    id: 'seller.focusCart',
    combination: { matchOn: 'key', value: 'F4' },
    scope: 'seller',
    keycapLabels: ['F4'],
    labelKey: action('focusCart'),
};

/**
 * Opens the discount dialog for the focused cart line, or the last line when
 * none is focused; an empty cart shows a notice instead. See `useCartPanel`/
 * `DiscountDialog`.
 */
export const LINE_DISCOUNT_SHORTCUT: ShortcutDefinition = {
    id: 'seller.lineDiscount',
    combination: { matchOn: 'key', value: 'F6' },
    scope: 'seller',
    keycapLabels: ['F6'],
    labelKey: action('lineDiscount'),
};

/** Opens the discount dialog for the whole sale; an empty cart shows a notice instead. */
export const SALE_DISCOUNT_SHORTCUT: ShortcutDefinition = {
    id: 'seller.saleDiscount',
    combination: { matchOn: 'key', value: 'F7' },
    scope: 'seller',
    keycapLabels: ['F7'],
    labelKey: action('saleDiscount'),
};

/**
 * Opens the void-confirm dialog. `matchOn: 'key'`, like the other named-key
 * bindings (`Delete` reports the same value in every layout); `firesWhileEditable`
 * so it works mid-search the way `LOCK_TILL_SHORTCUT` does — Ctrl+Delete would
 * otherwise just delete a word in the search field. See `CartPanel`/`VoidConfirmDialog`.
 */
export const VOID_SHORTCUT: ShortcutDefinition = {
    id: 'seller.void',
    combination: { matchOn: 'key', value: 'Delete', ctrlKey: true },
    scope: 'seller',
    keycapLabels: ['Ctrl', 'Del'],
    labelKey: action('void'),
    firesWhileEditable: true,
};

/** Opens the payment modal. See `CartPanel`/`PaymentModal`. */
export const PAY_SHORTCUT: ShortcutDefinition = {
    id: 'seller.pay',
    combination: { matchOn: 'key', value: 'F9' },
    scope: 'seller',
    keycapLabels: ['F9'],
    labelKey: action('pay'),
};

/** Opens the shift dialog: open when none is open, close when one is. See `ShiftActionButton`/`ShiftModal`. */
export const SHIFT_SHORTCUT: ShortcutDefinition = {
    id: 'seller.shift',
    combination: { matchOn: 'key', value: 'F10' },
    scope: 'seller',
    keycapLabels: ['F10'],
    labelKey: action('shift'),
};

/**
 * Layered: closes an open overlay (handled by the overlay's own scope + its
 * native `Escape` handling, since a pushed scope suspends this one), else
 * clears non-empty search text, else moves focus home. See `ProductPanel`.
 *
 * This entry itself only ever runs with focus *outside* the search field: a
 * non-function-key binding doesn't fire while an editable element has focus
 * (see `ShortcutProvider`), so with focus *in* search, `Escape` is instead
 * handled by the field's own native clear-on-`Escape` behaviour (its
 * `onChange` is wired straight to `setSearch`, so that already satisfies
 * "clears non-empty search text"); with focus in an *empty* search field
 * there's nothing to do either way, since search is already home.
 */
export const SALE_ESCAPE_SHORTCUT: ShortcutDefinition = {
    id: 'seller.escape',
    combination: { matchOn: 'key', value: 'Escape' },
    scope: 'seller',
    keycapLabels: ['Esc'],
    labelKey: action('escape'),
};

/**
 * `matchOn: 'code'`, so it holds under the Georgian layout like any letter
 * combination. `firesWhileEditable` so stepping away works even mid-search —
 * the one thing this shortcut is for.
 */
export const LOCK_TILL_SHORTCUT: ShortcutDefinition = {
    id: 'seller.lockTill',
    combination: {
        matchOn: 'code',
        value: 'KeyL',
        ctrlKey: true,
        shiftKey: true,
    },
    scope: 'seller',
    keycapLabels: ['Ctrl', 'Shift', 'L'],
    labelKey: action('lockTill'),
    firesWhileEditable: true,
};

/** Search `Enter` (`useSearchEnterToAdd`): the exact barcode match or the one result left; with several, focus moves into the grid. */
export const SEARCH_ADD_SHORTCUT: ShortcutDefinition = {
    id: 'seller.search.add',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: 'seller',
    keycapLabels: ['Enter'],
    labelKey: action('searchAdd'),
    displayOnly: true,
};

/** `↓` from search into the product grid (`useProductPanel`). */
export const SEARCH_TO_GRID_SHORTCUT: ShortcutDefinition = {
    id: 'seller.search.toGrid',
    combination: { matchOn: 'key', value: 'ArrowDown' },
    scope: 'seller',
    keycapLabels: ['↓'],
    labelKey: action('searchToGrid'),
    displayOnly: true,
};

/** The product grid's own two-dimensional arrow keys (`ProductGrid`, a design-system list box). */
export const GRID_MOVE_SHORTCUT: ShortcutDefinition = {
    id: 'seller.grid.move',
    combination: { matchOn: 'key', value: 'ArrowLeft' },
    scope: 'seller',
    keycapLabels: ['← / → / ↑ / ↓'],
    labelKey: action('gridMove'),
    displayOnly: true,
};

export const GRID_JUMP_SHORTCUT: ShortcutDefinition = {
    id: 'seller.grid.jump',
    combination: { matchOn: 'key', value: 'Home' },
    scope: 'seller',
    keycapLabels: ['Home / End'],
    labelKey: action('gridJump'),
    displayOnly: true,
};

/** `Enter` on a tile adds it and keeps focus there (`useProductGridActivation`). */
export const GRID_ADD_SHORTCUT: ShortcutDefinition = {
    id: 'seller.grid.add',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: 'seller',
    keycapLabels: ['Enter'],
    labelKey: action('gridAdd'),
    displayOnly: true,
};

/** The cart `ListView`'s own `↑`/`↓` between lines. */
export const CART_LINE_MOVE_SHORTCUT: ShortcutDefinition = {
    id: 'seller.cartLine.move',
    combination: { matchOn: 'key', value: 'ArrowUp' },
    scope: 'seller',
    keycapLabels: ['↑ / ↓'],
    labelKey: action('cartLineMove'),
    displayOnly: true,
};

/** Row-level `+`/`-` (`useCartKeyboardNav`), within the stepper's stock ceiling. */
export const CART_LINE_QUANTITY_SHORTCUT: ShortcutDefinition = {
    id: 'seller.cartLine.quantity',
    combination: { matchOn: 'key', value: '+' },
    scope: 'seller',
    keycapLabels: ['+ / -'],
    labelKey: action('cartLineQuantity'),
    displayOnly: true,
};

/** Row-level `Delete` (`useCartKeyboardNav`); `Ctrl+Delete` is `VOID_SHORTCUT`. */
export const CART_LINE_REMOVE_SHORTCUT: ShortcutDefinition = {
    id: 'seller.cartLine.remove',
    combination: { matchOn: 'key', value: 'Delete' },
    scope: 'seller',
    keycapLabels: ['Del'],
    labelKey: action('cartLineRemove'),
    displayOnly: true,
};

/** The cart `ListView`'s own `→`/`←` into and out of a line's stepper and remove button. */
export const CART_LINE_CONTROLS_SHORTCUT: ShortcutDefinition = {
    id: 'seller.cartLine.controls',
    combination: { matchOn: 'key', value: 'ArrowRight' },
    scope: 'seller',
    keycapLabels: ['→ / ←'],
    labelKey: action('cartLineControls'),
    displayOnly: true,
};

/** A letter or digit typed in the grid or the cart goes into search (`isPrintableKey`). */
export const TYPE_TO_SEARCH_SHORTCUT: ShortcutDefinition = {
    id: 'seller.typeToSearch',
    combination: { matchOn: 'code', value: 'KeyA' },
    scope: 'seller',
    keycapLabels: ['A-Z / 0-9'],
    labelKey: action('typeToSearch'),
    displayOnly: true,
};

/**
 * Pushed while the category filter's popover is open, suspending the rest of
 * the `seller` scope. Its keys are the design system's own `Autocomplete`:
 * the popover's search field has focus, the list follows `↑`/`↓`.
 */
export const CATEGORY_FILTER_SCOPE: ShortcutScope = 'sellerCategoryFilter';

export const CATEGORY_FILTER_TYPE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerCategoryFilter.type',
    combination: { matchOn: 'code', value: 'KeyA' },
    scope: CATEGORY_FILTER_SCOPE,
    keycapLabels: ['A-Z'],
    labelKey: action('categoryFilterType'),
    displayOnly: true,
};

export const CATEGORY_FILTER_MOVE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerCategoryFilter.move',
    combination: { matchOn: 'key', value: 'ArrowUp' },
    scope: CATEGORY_FILTER_SCOPE,
    keycapLabels: ['↑ / ↓'],
    labelKey: action('categoryFilterMove'),
    displayOnly: true,
};

export const CATEGORY_FILTER_TOGGLE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerCategoryFilter.toggle',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: CATEGORY_FILTER_SCOPE,
    keycapLabels: ['Enter'],
    labelKey: action('categoryFilterToggle'),
    displayOnly: true,
};

export const CATEGORY_FILTER_CLOSE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerCategoryFilter.close',
    combination: { matchOn: 'key', value: 'Escape' },
    scope: CATEGORY_FILTER_SCOPE,
    keycapLabels: ['Esc'],
    labelKey: action('categoryFilterClose'),
    displayOnly: true,
};

/**
 * Pushed while the variant picker is open, suspending the rest of the
 * `seller` scope. Its keys are the picker's own list box and dialog.
 */
export const VARIANT_PICKER_SCOPE: ShortcutScope = 'sellerVariantPicker';

export const VARIANT_PICKER_MOVE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerVariantPicker.move',
    combination: { matchOn: 'key', value: 'ArrowUp' },
    scope: VARIANT_PICKER_SCOPE,
    keycapLabels: ['↑ / ↓'],
    labelKey: action('variantMove'),
    displayOnly: true,
};

export const VARIANT_PICKER_CHOOSE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerVariantPicker.choose',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: VARIANT_PICKER_SCOPE,
    keycapLabels: ['Enter'],
    labelKey: action('variantChoose'),
    displayOnly: true,
};

export const VARIANT_PICKER_CLOSE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerVariantPicker.close',
    combination: { matchOn: 'key', value: 'Escape' },
    scope: VARIANT_PICKER_SCOPE,
    keycapLabels: ['Esc'],
    labelKey: action('variantClose'),
    displayOnly: true,
};

/**
 * Pushed while the void-confirm dialog is open, suspending the rest of the
 * `seller` scope. Confirm has focus on open, so `Enter` presses it; `Esc` is
 * the dialog's own dismiss. Both are shown in its footer.
 */
export const VOID_CONFIRM_SCOPE: ShortcutScope = 'sellerVoidConfirm';

export const VOID_CONFIRM_ACCEPT_SHORTCUT: ShortcutDefinition = {
    id: 'sellerVoidConfirm.confirm',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: VOID_CONFIRM_SCOPE,
    keycapLabels: ['Enter'],
    labelKey: action('voidConfirm'),
    displayOnly: true,
};

export const VOID_CONFIRM_CANCEL_SHORTCUT: ShortcutDefinition = {
    id: 'sellerVoidConfirm.cancel',
    combination: { matchOn: 'key', value: 'Escape' },
    scope: VOID_CONFIRM_SCOPE,
    keycapLabels: ['Esc'],
    labelKey: action('voidCancel'),
    displayOnly: true,
};

/**
 * Pushed while the discount dialog is open, suspending the rest of the
 * `seller` scope. `Enter` is `useDiscountDialogKeys` (it applies unless a
 * button has focus), `Esc` is the dialog's own dismiss, and `Tab` reaches the
 * Remove button, which has no key of its own.
 */
export const DISCOUNT_SCOPE: ShortcutScope = 'sellerDiscount';

/** Shown on the footer's Apply. */
export const DISCOUNT_APPLY_SHORTCUT: ShortcutDefinition = {
    id: 'sellerDiscount.apply',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: DISCOUNT_SCOPE,
    keycapLabels: ['Enter'],
    labelKey: action('discountApply'),
    displayOnly: true,
};

/** Shown on the footer's Cancel. */
export const DISCOUNT_CANCEL_SHORTCUT: ShortcutDefinition = {
    id: 'sellerDiscount.cancel',
    combination: { matchOn: 'key', value: 'Escape' },
    scope: DISCOUNT_SCOPE,
    keycapLabels: ['Esc'],
    labelKey: action('discountCancel'),
    displayOnly: true,
};

export const DISCOUNT_REMOVE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerDiscount.remove',
    combination: { matchOn: 'key', value: 'Tab' },
    scope: DISCOUNT_SCOPE,
    keycapLabels: ['Tab'],
    labelKey: action('discountRemove'),
    displayOnly: true,
};

/**
 * Pushed while the payment dialog is open, suspending the rest of the
 * `seller` scope. Also the scope `usePaymentPhysicalKeys` checks is live
 * before feeding the numpad or confirming, the same way `SELLER_LOGIN_SCOPE`
 * gates `usePinPhysicalKeys`. `←`/`→` are the method radio group's own,
 * `Tab`/`↑`/`↓` between the split legs are `useCombinedLegKeys`, and `Esc`
 * is the dialog's own dismiss.
 */
export const PAYMENT_SCOPE: ShortcutScope = 'sellerPayment';

export const PAYMENT_METHOD_SHORTCUT: ShortcutDefinition = {
    id: 'sellerPayment.method',
    combination: { matchOn: 'key', value: 'ArrowLeft' },
    scope: PAYMENT_SCOPE,
    keycapLabels: ['← / →'],
    labelKey: action('paymentMethod'),
    displayOnly: true,
};

export const PAYMENT_AMOUNT_SHORTCUT: ShortcutDefinition = {
    id: 'sellerPayment.amount',
    combination: { matchOn: 'key', value: '0' },
    scope: PAYMENT_SCOPE,
    keycapLabels: ['0-9 / . / ,'],
    labelKey: action('paymentAmount'),
    displayOnly: true,
};

export const PAYMENT_BACKSPACE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerPayment.backspace',
    combination: { matchOn: 'key', value: 'Backspace' },
    scope: PAYMENT_SCOPE,
    keycapLabels: ['Backspace'],
    labelKey: action('paymentBackspace'),
    displayOnly: true,
};

/** Confirms unless a button has focus; an empty tender means the exact amount. Shown on the footer's Confirm. */
export const PAYMENT_CONFIRM_SHORTCUT: ShortcutDefinition = {
    id: 'sellerPayment.confirm',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: PAYMENT_SCOPE,
    keycapLabels: ['Enter'],
    labelKey: action('paymentConfirm'),
    displayOnly: true,
};

/** Shown on the footer's Cancel. */
export const PAYMENT_CANCEL_SHORTCUT: ShortcutDefinition = {
    id: 'sellerPayment.cancel',
    combination: { matchOn: 'key', value: 'Escape' },
    scope: PAYMENT_SCOPE,
    keycapLabels: ['Esc'],
    labelKey: action('paymentCancel'),
    displayOnly: true,
};

export const PAYMENT_LEG_SHORTCUT: ShortcutDefinition = {
    id: 'sellerPayment.leg',
    combination: { matchOn: 'key', value: 'Tab' },
    scope: PAYMENT_SCOPE,
    keycapLabels: ['Tab / ↑ / ↓'],
    labelKey: action('paymentLeg'),
    displayOnly: true,
};

/**
 * Pushed while the shift dialog is open, the admin co-sign included,
 * suspending the rest of the `seller` scope. `useShiftDialogKeys` runs the
 * current step on `Enter`; the count is a real `NumberField`; the approver
 * picker is a list box; the co-sign PIN is `usePinPhysicalKeys`, gated on
 * this scope rather than `SELLER_LOGIN_SCOPE`.
 */
export const SHIFT_SCOPE: ShortcutScope = 'sellerShift';

export const SHIFT_COUNT_SHORTCUT: ShortcutDefinition = {
    id: 'sellerShift.count',
    combination: { matchOn: 'key', value: '0' },
    scope: SHIFT_SCOPE,
    keycapLabels: ['0-9 / .'],
    labelKey: action('shiftCount'),
    displayOnly: true,
};

/** Shown on the footer's primary action, whichever step it is. */
export const SHIFT_CONFIRM_SHORTCUT: ShortcutDefinition = {
    id: 'sellerShift.confirm',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: SHIFT_SCOPE,
    keycapLabels: ['Enter'],
    labelKey: action('shiftConfirm'),
    displayOnly: true,
};

/** The dialog's own dismiss; shown on the count step's Cancel. On the co-sign PIN, `Esc` is `SHIFT_PIN_CLEAR_SHORTCUT` instead. */
export const SHIFT_CANCEL_SHORTCUT: ShortcutDefinition = {
    id: 'sellerShift.cancel',
    combination: { matchOn: 'key', value: 'Escape' },
    scope: SHIFT_SCOPE,
    keycapLabels: ['Esc'],
    labelKey: action('shiftCancel'),
    displayOnly: true,
};

export const SHIFT_APPROVER_MOVE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerShift.approver.move',
    combination: { matchOn: 'key', value: 'ArrowLeft' },
    scope: SHIFT_SCOPE,
    keycapLabels: ['← / → / ↑ / ↓'],
    labelKey: action('approverMove'),
    displayOnly: true,
};

export const SHIFT_APPROVER_JUMP_SHORTCUT: ShortcutDefinition = {
    id: 'sellerShift.approver.jump',
    combination: { matchOn: 'code', value: 'KeyA' },
    scope: SHIFT_SCOPE,
    keycapLabels: ['A-Z'],
    labelKey: action('approverJump'),
    displayOnly: true,
};

export const SHIFT_APPROVER_PICK_SHORTCUT: ShortcutDefinition = {
    id: 'sellerShift.approver.pick',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: SHIFT_SCOPE,
    keycapLabels: ['Enter'],
    labelKey: action('approverPick'),
    displayOnly: true,
};

export const SHIFT_PIN_DIGIT_SHORTCUT: ShortcutDefinition = {
    id: 'sellerShift.pin.digit',
    combination: { matchOn: 'key', value: '0' },
    scope: SHIFT_SCOPE,
    keycapLabels: ['0-9'],
    labelKey: action('pinDigit'),
    displayOnly: true,
};

export const SHIFT_PIN_BACKSPACE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerShift.pin.backspace',
    combination: { matchOn: 'key', value: 'Backspace' },
    scope: SHIFT_SCOPE,
    keycapLabels: ['Backspace'],
    labelKey: action('pinBackspace'),
    displayOnly: true,
};

export const SHIFT_PIN_CLEAR_SHORTCUT: ShortcutDefinition = {
    id: 'sellerShift.pin.clear',
    combination: { matchOn: 'key', value: 'Escape' },
    scope: SHIFT_SCOPE,
    keycapLabels: ['Esc'],
    labelKey: action('pinClear'),
    displayOnly: true,
};

/**
 * Pushed by the whole sign-in screen (`SellerLogin`) on both steps, the
 * roster and the PIN. The roster's keys are its own list box's; the PIN's
 * are `usePinPhysicalKeys`, gated on this scope (a PIN keystroke carries a
 * digit, not a fixed command, so it doesn't fit `useShortcut`'s
 * one-combination-per-id model; `PIN_DIGIT_SHORTCUT`'s `'0'` stands in for
 * the whole `0`-`9` range).
 */
export const SELLER_LOGIN_SCOPE: ShortcutScope = 'sellerLogin';

export const ROSTER_MOVE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerLogin.roster.move',
    combination: { matchOn: 'key', value: 'ArrowLeft' },
    scope: SELLER_LOGIN_SCOPE,
    keycapLabels: ['← / → / ↑ / ↓'],
    labelKey: action('rosterMove'),
    displayOnly: true,
};

export const ROSTER_ENDS_SHORTCUT: ShortcutDefinition = {
    id: 'sellerLogin.roster.ends',
    combination: { matchOn: 'key', value: 'Home' },
    scope: SELLER_LOGIN_SCOPE,
    keycapLabels: ['Home / End'],
    labelKey: action('rosterEnds'),
    displayOnly: true,
};

export const ROSTER_JUMP_SHORTCUT: ShortcutDefinition = {
    id: 'sellerLogin.roster.jump',
    combination: { matchOn: 'code', value: 'KeyA' },
    scope: SELLER_LOGIN_SCOPE,
    keycapLabels: ['A-Z'],
    labelKey: action('rosterJump'),
    displayOnly: true,
};

export const ROSTER_PICK_SHORTCUT: ShortcutDefinition = {
    id: 'sellerLogin.roster.pick',
    combination: { matchOn: 'key', value: 'Enter' },
    scope: SELLER_LOGIN_SCOPE,
    keycapLabels: ['Enter'],
    labelKey: action('rosterPick'),
    displayOnly: true,
};

export const PIN_DIGIT_SHORTCUT: ShortcutDefinition = {
    id: 'sellerLogin.pin.digit',
    combination: { matchOn: 'key', value: '0' },
    scope: SELLER_LOGIN_SCOPE,
    keycapLabels: ['0-9'],
    labelKey: action('pinDigit'),
    displayOnly: true,
};

export const PIN_BACKSPACE_SHORTCUT: ShortcutDefinition = {
    id: 'sellerLogin.pin.backspace',
    combination: { matchOn: 'key', value: 'Backspace' },
    scope: SELLER_LOGIN_SCOPE,
    keycapLabels: ['Backspace'],
    labelKey: action('pinBackspace'),
    displayOnly: true,
};

export const PIN_CLEAR_SHORTCUT: ShortcutDefinition = {
    id: 'sellerLogin.pin.clear',
    combination: { matchOn: 'key', value: 'Escape' },
    scope: SELLER_LOGIN_SCOPE,
    keycapLabels: ['Esc'],
    labelKey: action('pinClear'),
    displayOnly: true,
};

/**
 * Single source of truth for every key on the seller path: the provider's
 * matching (bound entries), the hints, and the cheat sheet (every entry,
 * grouped by scope in this order after the current one) all read from this
 * table. Guarded by `src/tests/conventions/shortcutRegistry.guard.test.ts`.
 */
export const shortcutRegistry: ShortcutDefinition[] = [
    FOCUS_SEARCH_SHORTCUT,
    CATEGORY_FILTER_SHORTCUT,
    FOCUS_CART_SHORTCUT,
    LINE_DISCOUNT_SHORTCUT,
    SALE_DISCOUNT_SHORTCUT,
    VOID_SHORTCUT,
    PAY_SHORTCUT,
    SHIFT_SHORTCUT,
    SALE_ESCAPE_SHORTCUT,
    LOCK_TILL_SHORTCUT,
    SEARCH_ADD_SHORTCUT,
    SEARCH_TO_GRID_SHORTCUT,
    GRID_MOVE_SHORTCUT,
    GRID_JUMP_SHORTCUT,
    GRID_ADD_SHORTCUT,
    CART_LINE_MOVE_SHORTCUT,
    CART_LINE_QUANTITY_SHORTCUT,
    CART_LINE_REMOVE_SHORTCUT,
    CART_LINE_CONTROLS_SHORTCUT,
    TYPE_TO_SEARCH_SHORTCUT,
    CATEGORY_FILTER_TYPE_SHORTCUT,
    CATEGORY_FILTER_MOVE_SHORTCUT,
    CATEGORY_FILTER_TOGGLE_SHORTCUT,
    CATEGORY_FILTER_CLOSE_SHORTCUT,
    VARIANT_PICKER_MOVE_SHORTCUT,
    VARIANT_PICKER_CHOOSE_SHORTCUT,
    VARIANT_PICKER_CLOSE_SHORTCUT,
    VOID_CONFIRM_ACCEPT_SHORTCUT,
    VOID_CONFIRM_CANCEL_SHORTCUT,
    DISCOUNT_APPLY_SHORTCUT,
    DISCOUNT_CANCEL_SHORTCUT,
    DISCOUNT_REMOVE_SHORTCUT,
    PAYMENT_METHOD_SHORTCUT,
    PAYMENT_AMOUNT_SHORTCUT,
    PAYMENT_BACKSPACE_SHORTCUT,
    PAYMENT_CONFIRM_SHORTCUT,
    PAYMENT_CANCEL_SHORTCUT,
    PAYMENT_LEG_SHORTCUT,
    SHIFT_COUNT_SHORTCUT,
    SHIFT_CONFIRM_SHORTCUT,
    SHIFT_CANCEL_SHORTCUT,
    SHIFT_APPROVER_MOVE_SHORTCUT,
    SHIFT_APPROVER_JUMP_SHORTCUT,
    SHIFT_APPROVER_PICK_SHORTCUT,
    SHIFT_PIN_DIGIT_SHORTCUT,
    SHIFT_PIN_BACKSPACE_SHORTCUT,
    SHIFT_PIN_CLEAR_SHORTCUT,
    CHEAT_SHEET_SHORTCUT,
    ROSTER_MOVE_SHORTCUT,
    ROSTER_ENDS_SHORTCUT,
    ROSTER_JUMP_SHORTCUT,
    ROSTER_PICK_SHORTCUT,
    PIN_DIGIT_SHORTCUT,
    PIN_BACKSPACE_SHORTCUT,
    PIN_CLEAR_SHORTCUT,
];
